import React from "react";

/**
 * Shared footer schema component
 * Defines Organization and Hotel entities that appear on every page
 * Other page schemas should reference these entities by @id
 * 
 * This follows Google's best practice of having LocalBusiness/Organization
 * schema on every page for consistent entity recognition
 */
export function FooterSchema({ footerData }) {
  const baseUrl = footerData?.organizationUrl || "https://unioninn.com";

  // Helper to parse address
  const parseAddress = (coreData) => {
    const address2 = coreData?.HotelAddress2 || "Nantucket, MA 02554";
    const parts = address2.split(",").map((s) => s.trim());
    const city = parts[0] || "Nantucket";
    const stateZip = parts[1] || "MA 02554";
    const [state, postalCode] = stateZip.split(" ").filter(Boolean);

    return {
      streetAddress: coreData?.HotelAddress1 || "7 Union Street",
      addressLocality: city,
      addressRegion: state || "MA",
      postalCode: postalCode || "02554",
      addressCountry: "US",
    };
  };

  // Build @graph with Organization and Hotel entities
  const graph = [];

  // 1. Organization entity (appears on every page)
  graph.push({
    "@type": "Organization",
    "@id": `${baseUrl}#organization`,
    name: footerData?.HotelName || "Union Street Inn",
    url: baseUrl,
    logo: {
      "@type": "ImageObject",
      url: `${baseUrl}/logo-home.png`,
    },
    contactPoint: {
      "@type": "ContactPoint",
      telephone: footerData?.PhoneLocal?.replace(/\s+/g, "") || "+15082281255",
      email: footerData?.Email || "info@unioninn.com",
      contactType: "Customer Service",
    },
    address: {
      "@type": "PostalAddress",
      ...parseAddress(footerData),
    },
    ...(() => {
      const socialMedia = [
        footerData?.Facebook,
        footerData?.Instagram,
        footerData?.TripAdvisor,
      ].filter(Boolean);
      return socialMedia.length > 0 ? { sameAs: socialMedia } : {};
    })(),
  });

  // 2. Hotel entity (appears on every page)
  graph.push({
    "@type": "Hotel",
    "@id": `${baseUrl}#hotel`,
    name: footerData?.HotelName || "Union Street Inn",
    url: baseUrl,
    geo: {
      "@type": "GeoCoordinates",
      latitude: 41.2835,
      longitude: -70.0995,
    },
    address: {
      "@type": "PostalAddress",
      ...parseAddress(footerData),
    },
    telephone: footerData?.PhoneLocal?.replace(/\s+/g, "") || "+15082281255",
    email: footerData?.Email || "info@unioninn.com",
    priceRange: "$$$",
    starRating: {
      "@type": "Rating",
      ratingValue: 4,
      bestRating: 5,
    },
    checkinTime: "15:00",
    checkoutTime: "11:00",
  });

  const consolidatedSchema = {
    "@context": "https://schema.org",
    "@graph": graph,
  };

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{
        __html: JSON.stringify(consolidatedSchema, null, 2),
      }}
    />
  );
}

